import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, User, Calendar, ExternalLink, BookOpen } from 'lucide-react';
import SEO from '../components/shared/SEO';

// Article content database
const ARTICLE_CONTENT = {
  'new-authority-first-90-days': {
    title: 'What Every New Authority Must Do in the First 90 Days',
    subtitle: 'Your first 90 days will determine your first 3 years. Here\'s what serious operators focus on immediately.',
    category: 'Authority & Compliance',
    subcategory: 'New Authority',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1600&h=800&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '10 min read',
    pullQuote: 'Your first 90 days will determine your first 3 years.',
    content: `
## Your First 90 Days Will Determine Your First 3 Years

Getting your MC number feels like the hard part.

It's not.

The first 90 days after activation are where most new authorities either:

- Build strong foundations
- Or create expensive mistakes

New authority doesn't fail because of lack of hustle.

**It fails because of lack of structure.**

Here's what serious operators focus on immediately.

---

## The 7 Pillars of a Strong New Authority

### 1. Secure Proper Insurance (Not Just the Minimum)

Most new carriers only focus on:

- $1M liability
- $100K cargo

But what you should focus on is:

- Broker requirements
- Contract requirements
- Financial protection

**Underinsured carriers get rejected.**

**Overleveraged carriers get crushed by deductibles.**

In your first 90 days, review:

- Coverage limits
- Deductible amounts
- Physical damage protection
- Breakdown risk planning

**Insurance is protection — not just compliance.**

---

### 2. Separate Business & Personal Finances Immediately

If you are running loads through your personal bank account, stop.

You need:

- Business checking account
- Dedicated business debit/credit
- Clear expense tracking
- Merchant processing structure

**Why?**

Because when tax season hits, chaos costs money.

**Professional carriers build financial clarity from Day 1.**

---

### 3. Establish a Digital Operations System

You should not be operating from:

- Text message screenshots
- Random Gmail threads
- Paper folders in the truck

In the first 90 days, build:

- Organized cloud storage
- Document folders (BOLs, rate cons, insurance, permits)
- Expense tracking spreadsheet
- Load tracking system

**Structure reduces stress.**

**Stress causes bad decisions.**

---

### 4. Monitor Your SAFER & FMCSA Profile

Your public record matters.

Brokers look at:

- Inspection history
- Safety score
- Out-of-service violations
- Insurance status

In your first 90 days:

- Avoid preventable violations
- Maintain equipment
- Review logs carefully
- Check your public profile monthly

**Reputation builds early.**

**And it sticks.**

---

### 5. Be Strategic About Load Selection

Many new authorities make this mistake:

*They accept anything to keep moving.*

But your early loads affect:

- Broker relationships
- Profit margin
- Cash flow stability
- Market positioning

Focus on:

- Fair rate per mile
- Strong lanes
- Reliable brokers
- Low-risk freight

**The first freight you run sets your baseline expectations.**

---

### 6. Protect Your Cash Flow

Cash flow kills more new authorities than fuel prices.

In your first 90 days:

- Track every expense
- Know your cost per mile
- Avoid emotional purchases
- Plan for maintenance reserves

*If you don't know your break-even number, you're gambling.*

**Serious operators calculate.**

---

### 7. Build Relationships — Not Just Revenue

Your first 90 days are about positioning.

Build:

- Broker relationships
- Dispatcher partnerships
- Compliance habits
- Equipment discipline

Because your authority age grows whether you prepare or not.

**The question is: Will you grow with it?**

---

## The Truth About New Authorities

The carriers who survive long-term aren't the fastest movers.

**They're the most structured.**

They:

- Separate business from emotion
- Operate legally and clean
- Protect profit margins
- Plan ahead

**Your authority is not just permission to operate.**

**It's a business license.**

Treat it like one.
    `,
    affiliateSection: null,
    relatedArticles: ['reduce-deadhead-without-killing-profit', 'google-workspace-for-carriers'],
  },
  'why-we-call-it-premium-freight-operations': {
    title: 'Why We Call It Premium Freight Operations',
    subtitle: 'We\'re not just dispatchers. What we provide goes beyond finding freight — it\'s structure, positioning, and long-term strategy.',
    category: 'The Queen B Philosophy',
    subcategory: 'Our Approach',
    image: 'https://images.unsplash.com/photo-1599907450334-45fc51a51cee?w=1600&h=800&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '7 min read',
    pullQuote: 'We don\'t describe what we do as dispatching. We call it Premium Freight Operations.',
    content: `
## We're Not Just Dispatchers.

There's a difference between:

*Booking loads*

And operating freight strategically.

At Queen B Logistics, we don't describe what we do as "dispatching."

**We call it Premium Freight Operations.**

Because what we provide goes beyond finding freight.

It's structure.
It's positioning.
It's long-term strategy.

**And not every carrier is built for it.**

---

## The Problem With Traditional Dispatch

Most dispatch models are reactive.

- Wait for load
- Book load
- Move truck
- Repeat

There's no:

- Market positioning
- Profit forecasting
- Compliance oversight
- Scaling roadmap

**It's motion without direction.**

**Busy doesn't equal profitable.**

---

## What Premium Freight Operations Actually Means

Premium Freight Operations focuses on:

### 1. Strategic Lane Planning

We don't just book the next available load.

We position trucks in markets that maximize reload potential and minimize deadhead.

**Every move is intentional.**

---

### 2. Market Awareness

We monitor freight trends, seasonal shifts, and rate fluctuations.

This allows us to advise carriers on:

- When to push for higher rates
- When to secure consistent lanes
- When to avoid weak markets

**Knowledge is leverage.**

---

### 3. Revenue Optimization

We analyze total trip profitability — not just rate per mile.

This includes:

- Fuel costs
- Deadhead positioning
- Time efficiency
- Reload probability

**Gross revenue means nothing without margin.**

---

### 4. Compliance Protection

We help carriers maintain:

- Clean SAFER records
- Proper documentation
- Insurance compliance
- ELD accuracy

**Compliance is protection — not paperwork.**

---

### 5. Long-Term Growth

We don't just help carriers survive.

We help them scale.

This means:

- Building broker relationships
- Strengthening lane consistency
- Creating financial discipline
- Preparing for fleet expansion

**Growth requires structure.**

---

## We Think in Systems — Not Loads

Every decision is connected.

When we book freight, we consider:

- Reload probability
- Market strength
- Deadhead positioning
- Broker relationship value
- Rate stability

**This is not random booking.**

**It's controlled movement.**

---

## Not Every Carrier Is a Good Fit

We are selective.

Because serious operations require:

- Financial discipline
- Documentation habits
- Professional communication
- Growth mindset

If a carrier is disorganized, emotional, or inconsistent — no system can fix that.

**Structure requires cooperation.**

**Premium freight requires standards.**

---

## The Hive Standard

At Queen B Logistics, we operate by what we call:

**The Hive Standard.**

It means:

- Plan 2 loads ahead
- Protect compliance
- Track profit intentionally
- Separate business from emotion
- Scale only when structured

**The Hive isn't about volume.**

**It's about elevation.**

---

## Why This Matters

Trucking is easy to enter.

But difficult to sustain.

The difference between a short-term operator and a long-term company is philosophy.

Most carriers focus on:

*"How do I get paid this week?"*

We focus on:

**"How do we build a structured, profitable operation that lasts?"**

That's premium freight operations.

---

## The Truth

You don't need more loads.

You need:

- Better positioning
- Cleaner systems
- Stronger compliance
- Calculated growth

**That's what we build.**
    `,
    affiliateSection: null,
    relatedArticles: ['new-authority-first-90-days', 'when-to-add-second-truck'],
  },
  'when-to-add-second-truck': {
    title: 'When to Add Your Second Truck (And When You\'re Not Ready Yet)',
    subtitle: 'Adding a second truck is not a flex — it\'s a financial decision. Learn how to know the difference.',
    category: 'Scaling From 1 Truck to a Fleet',
    subcategory: 'Fleet Growth',
    image: 'https://images.unsplash.com/photo-1619551734325-81aaf323686c?w=1600&h=800&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '10 min read',
    pullQuote: 'Your second truck should not fix your first truck\'s problems. It should multiply a working system.',
    content: `
## Adding a Second Truck Is Not a Flex — It's a Financial Decision

A lot of owner-operators say:

*"I'm ready to scale."*

But scaling isn't about excitement.

**It's about stability.**

Your second truck should not fix your first truck's problems.

It should multiply a working system.

If your first truck isn't structured and profitable, your second truck will double the chaos.

Let's break down how to know the difference.

---

## The 6-Step Scaling Framework

### Step 1: Your First Truck Must Be Predictably Profitable

Before you even think about expanding, ask yourself:

- Do I know my exact cost per mile?
- Do I know my average weekly net — not just gross?
- Do I maintain consistent lanes?
- Do I have maintenance reserves saved?

If your profit fluctuates wildly every week, you are not ready to scale.

**Predictability comes before expansion.**

---

### Step 2: You Need 3–6 Months of Financial Cushion

Adding a second truck means:

- Down payment or purchase cost
- Insurance increase
- Registration & permits
- Unexpected repairs
- Driver payroll
- Downtime risk

If your first breakdown would stress you out financially, scaling will amplify that stress.

**Serious operators build reserves before expanding.**

**Not after.**

---

### Step 3: Your Systems Must Be Repeatable

Scaling is not just adding equipment.

It's replicating structure.

Ask yourself:

- Is my load booking process documented?
- Is my expense tracking organized?
- Do I have a dispatcher system in place?
- Can someone else step into my role tomorrow?

If your business only works because you personally manage everything in your head, it cannot scale.

**Scaling requires systems.**

---

### Step 4: Decide — Owner-Operator or Fleet Owner?

Your second truck changes your role.

You are no longer just:

Driver + Operator.

You become:

**Manager + Strategist.**

Are you prepared to:

- Manage drivers?
- Handle payroll?
- Deal with driver turnover?
- Enforce compliance standards?

**Scaling requires leadership.**

**Not just ambition.**

---

### Step 5: Understand the True Cost of a Driver

A lot of new fleet owners underestimate this.

Driver cost includes:

- Pay (percentage or CPM)
- Workers' comp
- Insurance increase
- Downtime
- Recruiting cost
- Training time

If your driver does not produce consistent revenue, you absorb the loss.

**Profit margins shrink quickly without structure.**

---

### Step 6: The Smart Way to Scale

The most stable growth strategy looks like this:

- Stabilize first truck
- Build reserves
- Strengthen lanes
- Establish compliance habits
- Add second truck
- Monitor performance closely
- Adjust before adding third

**Scaling too fast is how small fleets collapse.**

**Controlled growth is how real companies form.**

---

## Signs You're Ready to Add a Second Truck

- 6+ months of stable revenue
- Clear financial tracking
- Maintenance reserve fund
- Structured dispatch process
- Strong broker relationships
- Emotional discipline

**Scaling should feel calculated — not desperate.**

---

## Signs You're Not Ready Yet

- You're still guessing your profit
- You rely on random spot market loads
- You don't have savings
- You're overwhelmed already
- You want to scale for status

**Growth without structure creates stress.**

**Structure before scale.**

---

## The Real Goal

The goal is not "more trucks."

The goal is:

- Higher net profit
- More stability
- Greater leverage
- Freedom of time

**If your second truck increases stress but not margin, it's not scaling — it's expansion without strategy.**
    `,
    affiliateSection: null,
    relatedArticles: ['new-authority-first-90-days', 'reduce-deadhead-without-killing-profit'],
  },
  'reduce-deadhead-without-killing-profit': {
    title: 'How to Reduce Deadhead Without Killing Profit',
    subtitle: 'Deadhead isn\'t the enemy — bad strategy is. Learn the profit formula serious carriers use.',
    category: 'Premium Freight Strategy',
    subcategory: 'Lane Optimization',
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1600&h=800&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '8 min read',
    pullQuote: 'Deadhead isn\'t the enemy — bad strategy is.',
    content: `
## Deadhead Isn't the Enemy — Bad Strategy Is.

Every carrier says they want to reduce deadhead.

But here's the truth:

Chasing "low deadhead" without understanding profit structure is how operators end up running cheap freight.

**Deadhead isn't what kills your business.**

**Uncalculated freight decisions do.**

Let's break it down correctly.

---

## What Deadhead Actually Is

Deadhead = miles driven without a paying load.

Common situations:
- Driving home after delivery
- Repositioning into a better market
- Leaving a weak freight zone
- Driving to secure higher-paying freight

The goal isn't "zero deadhead."

**The goal is profitable positioning.**

---

## The Dangerous Mistake Most Carriers Make

They take the closest load available.

Because:

*"It's only 10 miles away."*

But that load pays:
**$1.60 per mile.**

And the one 90 miles away?
**Pays $3.10 per mile.**

Let's do the math.

---

### Scenario A:

- 10-mile deadhead
- 500 loaded miles
- $1.60 per mile

**Gross: $800**

---

### Scenario B:

- 90-mile deadhead
- 500 loaded miles
- $3.10 per mile

**Gross: $1,550**

Even after fuel on 90 repositioning miles, Scenario B wins.

**Low deadhead doesn't always equal higher profit.**

**Higher revenue per mile does.**

---

## The 4 Strategic Ways to Reduce Deadhead (The Right Way)

### 1. Pre-Book Your Reload Before Delivery

Professional dispatch strategy means:

Your next load is already lined up before you drop the current one.

This reduces:
- Sitting time
- Market scrambling
- Weak rate acceptance

**Positioning matters more than proximity.**

---

### 2. Learn Strong vs Weak Freight Markets

Every market has:

- High outbound volume zones
- Low outbound volume zones

If you deliver into a weak market, expect deadhead.

If you deliver into Atlanta, Dallas, Chicago, etc., reload potential is higher.

**The real strategy: Deliver where freight flows.**

---

### 3. Negotiate for Market Exit

If you must deliver into a weaker market:

**Negotiate higher rate.**

You're not just pricing the load.

You're pricing:
- Repositioning risk
- Market imbalance
- Time cost

**Premium freight operators price for positioning.**

---

### 4. Use Deadhead as an Investment

Sometimes 75–120 miles of deadhead is strategic.

Because it puts you into:

- Higher-paying lanes
- Dedicated contracts
- Better reload density

**Deadhead is a tool — not a loss.**

---

## The Profit Formula Serious Carriers Use

Instead of asking:

*"How do I eliminate deadhead?"*

Ask:

**"What's my total trip profit?"**

Here's what matters:

- Loaded miles revenue
- Fuel cost (loaded + deadhead)
- Maintenance allocation
- Time
- Market positioning

**Total trip net > emotional reaction to empty miles.**

---

## Deadhead Control Systems

At Queen B Logistics, we analyze:

- Lane density
- Market trends
- Reload probability
- Rate per mile vs reposition cost

This is called **strategic lane optimization.**

Not random load booking.

---

## Final Thought

Zero deadhead is unrealistic.

Uncalculated freight is dangerous.

The carriers who grow are the ones who:

- Think 2 loads ahead
- Price repositioning correctly
- Deliver into strong markets
- Analyze profit per trip — not emotion per mile

**Deadhead isn't the problem.**

**Lack of strategy is.**
    `,
    affiliateSection: null,
    relatedArticles: ['google-workspace-for-carriers'],
  },
  'google-workspace-for-carriers': {
    title: 'Why Serious Carriers Operate on Google Workspace (And Not Gmail)',
    subtitle: 'If you\'re still running your trucking business from a personal Gmail account, you\'re operating like a side hustle — not a company.',
    category: 'Financial Growth for Carriers',
    subcategory: 'Systems & Infrastructure',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&h=800&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '8 min read',
    content: `
## The Difference Between a Driver and a Business Owner

There's a massive difference between:

- Someone who drives a truck
- Someone who runs a trucking company

**The difference is systems.**

Professional carriers don't operate from:
- @gmail.com
- Text-message load confirmations
- Random screenshots saved in their camera roll

They operate from structured digital infrastructure.

And one of the simplest upgrades you can make today is moving to **Google Workspace**.

---

## Why Google Workspace Is a Game-Changer for Owner-Operators

When you upgrade to Google Workspace, you immediately gain:

### 1. A Professional Business Email

Example:
- dispatch@yourcompany.com
- billing@yourcompany.com
- compliance@yourcompany.com

This builds credibility with:
- Brokers
- Shippers
- Factoring companies
- Insurance providers

**Professional email alone changes perception.**

---

### 2. Organized Digital Filing System

No more digging through texts for:
- Rate confirmations
- BOLs
- Insurance certificates
- IFTA documents

Google Drive allows you to create:
- 2026 Loads Folder
- Compliance Folder
- Insurance Documents
- Maintenance Records
- Expense Tracking

**Everything searchable. Everything secure.**

---

### 3. Shared Access Between Dispatcher & Carrier

If you're working with a dispatcher (like Queen B Logistics), shared folders allow:

- Real-time document access
- Instant upload of BOLs
- Centralized invoice tracking
- Clean bookkeeping flow

**No confusion. No lost paperwork.**

---

### 4. Integrated Sheets for Expense & Revenue Tracking

Google Sheets can function as:

- Load tracker
- Fuel tracker
- Maintenance log
- Settlement calculator

This is especially powerful for new authorities trying to understand:

*"What am I actually profiting?"*

---

### 5. Built-In Security & Business Protection

Google Workspace offers:
- Admin controls
- Two-factor authentication
- User permissions
- Secure cloud backup

**That's real business protection.**

---

## The Cost vs The Value

Most carriers will spend:

- $12,000–$25,000 on repairs
- Thousands on tires
- Thousands on fuel weekly

But hesitate to invest in a structured business system that costs less than dinner.

**That's backwards.**

---

## Which Plan Should a Carrier Choose?

### Business Starter
Best for:
- Solo owner-operators
- New authorities
- Basic document storage + email

### Business Standard
Best for:
- Carriers working with dispatchers
- Small fleets
- Companies needing more storage + collaboration

---

## The Premium Freight Standard

At Queen B Logistics, we operate on systems.

Because **serious freight requires serious structure.**

Google Workspace is not "just email."

**It's operational infrastructure.**

---

## Final Thought

If you want brokers to treat you like a professional, you must operate like one.

**Upgrade your business systems.**

Move differently this year.
    `,
    affiliateSection: {
      product: 'Google Workspace',
      link: 'https://referworkspace.app.goo.gl/FxrS',
      description: 'Get started with professional business email and cloud infrastructure.',
      promoCodes: [
        {
          plan: 'Google Workspace Business Starter',
          codes: ['E7KAQKJVGPMHCUJ', '7CGP7LCPMQXHC9J'],
          discount: '10% off your first year',
        },
        {
          plan: 'Google Workspace Business Standard',
          codes: ['EDGCDRHE6ET9KNU', 'E9E3QXQHATNG6ND'],
          discount: '10% off your first year',
        },
      ],
    },
    relatedArticles: ['one-financial-move-serious-carriers'],
  },
  'one-financial-move-serious-carriers': {
    title: 'One Financial Move That Separates Serious Carriers From Everyone Else',
    subtitle: 'Most new carriers focus on freight rates, load boards, and miles. But the carriers who actually build long-term wealth focus on something else first.',
    category: 'Financial Growth for Carriers',
    subcategory: 'Systems & Infrastructure',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&h=800&fit=crop',
    author: 'Queen B Logistics',
    date: 'February 2026',
    readTime: '6 min read',
    pullQuote: 'Your trucking business can\'t grow properly if your finances are disorganized.',
    content: `
## Most New Carriers Focus on the Wrong Things

Most new carriers focus on freight rates, load boards, and miles.

But the carriers who actually build long-term wealth focus on something else first:

**Financial infrastructure.**

Your trucking business can't grow properly if your finances are disorganized. The difference between a struggling operator and a scalable company often comes down to how money is managed behind the scenes.

Here's one of the simplest moves that instantly upgrades your business.

---

## Open a Dedicated Business Banking System

Too many owner-operators run their trucking income through personal accounts.

That creates problems when it comes to:

- Tracking fuel and maintenance expenses
- Paying taxes properly
- Building business credit
- Working with brokers and lenders
- Scaling to multiple trucks

A professional trucking operation should always run through a dedicated business checking account.

This allows you to clearly track:

- Load revenue
- Weekly settlements
- Fuel and maintenance spending
- Dispatch and compliance costs
- Profit margins

**It also makes tax season dramatically easier.**

---

## A Simple Option Many Small Businesses Use

Many carriers choose to open their first business account through major institutions like Chase because they offer banking tools specifically designed for small business owners.

Chase is currently offering a referral program where business owners can invite other entrepreneurs to open a qualifying business checking account.

If you're setting up your business banking or helping another entrepreneur do the same, you can explore it here:

[Open Chase Business Checking](https://accounts.chase.com/business/share/3248377200)

**Programs like this also reward referrals when other businesses open qualifying accounts.**

---

## Financial Discipline Creates Real Growth

The carriers who survive in trucking long-term aren't always the ones hauling the most loads.

**They're the ones who manage money the best.**

That means:

- Proper business banking
- Tracking every dollar coming in and out
- Planning for taxes ahead of time
- Keeping operating reserves
- Investing profits back into the company

When your finances are organized, every other part of the business becomes easier to scale.

---

## Build Your Operation Like a Real Company

Your truck may be the engine of your business, but your financial systems are the foundation.

Setting up professional banking, financial tracking, and disciplined money management early can save you thousands of dollars and countless headaches later.

The goal isn't just to run loads.

**The goal is to build a profitable, sustainable trucking business.**
    `,
    affiliateSection: {
      product: 'Chase Business Checking',
      link: 'https://accounts.chase.com/business/share/3248377200',
      description: 'Set up your dedicated business banking with Chase and get access to tools designed for small business owners.',
      promoCodes: null,
    },
    relatedArticles: ['google-workspace-for-carriers', 'new-authority-first-90-days'],
  },
};

const CarrierChroniclesArticlePage = () => {
  const { slug } = useParams();
  const article = ARTICLE_CONTENT[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-white text-2xl mb-4">Article not found</h1>
          <Link to="/carrier-chronicles" className="text-[#D4AF37] hover:underline">
            ← Back to The Carrier Chronicles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="article-page">
      <SEO 
        title={`${article.title} | The Carrier Chronicles`}
        description={article.subtitle}
        canonical={`https://queenblogistics.com/carrier-chronicles/${slug}`}
        keywords="trucking business systems, google workspace for trucking, professional carrier email, freight business infrastructure"
      />

      {/* Hero Image */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img 
          src={article.image} 
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        
        {/* Back button */}
        <div className="absolute top-6 left-6 z-10">
          <Link 
            to="/carrier-chronicles" 
            className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] transition-colors bg-black/30 backdrop-blur px-4 py-2"
          >
            <ArrowLeft className="w-4 h-4" />
            The Carrier Chronicles
          </Link>
        </div>
      </section>

      {/* Article Header */}
      <section className="relative -mt-32 z-10 max-w-4xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="bg-[#D4AF37] text-black px-3 py-1 text-xs font-bold uppercase tracking-wider">
            {article.category}
          </span>
          <span className="text-white/40 text-sm">{article.subcategory}</span>
        </div>
        
        <h1 className="font-['Playfair_Display',serif] text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
          {article.title}
        </h1>
        
        <p className="text-white/60 text-xl md:text-2xl mb-8 font-light italic">
          {article.subtitle}
        </p>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-white/40 border-b border-white/10 pb-8">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            {article.author}
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {article.date}
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {article.readTime}
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <article className="article-content prose prose-invert prose-lg max-w-none">
          <ArticleRenderer content={article.content} />
        </article>

        {/* Affiliate Section */}
        {article.affiliateSection && (
          <div className="my-16">
            <AffiliateBox affiliate={article.affiliateSection} />
          </div>
        )}
      </section>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10 py-4 z-40">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-white/60 text-sm hidden md:block">
            Ready to operate at a higher level?
          </p>
          <Link 
            to="/intake"
            className="btn-primary text-sm flex items-center gap-2"
          >
            Apply to Join The Hive
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Related Articles */}
      {article.relatedArticles && article.relatedArticles.length > 0 && (
        <section className="py-16 border-t border-white/10 mb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-['Playfair_Display',serif] text-2xl text-white mb-8">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Related articles would render here */}
            </div>
          </div>
        </section>
      )}

      {/* Back to Chronicles */}
      <section className="py-16 border-t border-white/10 mb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Link 
            to="/carrier-chronicles"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline"
          >
            <BookOpen className="w-5 h-5" />
            Back to The Carrier Chronicles
          </Link>
        </div>
      </section>
    </div>
  );
};

// Helper function to parse inline elements (bold, links)
const parseInlineElements = (text, keyPrefix = '') => {
  // First handle links [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const boldRegex = /\*\*([^*]+)\*\*/g;
  
  let parts = [];
  let lastIndex = 0;
  let match;
  
  // Find all links and bold text
  const allMatches = [];
  
  // Reset regex
  linkRegex.lastIndex = 0;
  while ((match = linkRegex.exec(text)) !== null) {
    allMatches.push({ type: 'link', index: match.index, length: match[0].length, text: match[1], url: match[2] });
  }
  
  boldRegex.lastIndex = 0;
  while ((match = boldRegex.exec(text)) !== null) {
    allMatches.push({ type: 'bold', index: match.index, length: match[0].length, text: match[1] });
  }
  
  // Sort by index
  allMatches.sort((a, b) => a.index - b.index);
  
  // Build parts array
  allMatches.forEach((m, idx) => {
    if (m.index > lastIndex) {
      parts.push(<span key={`${keyPrefix}-text-${idx}`}>{text.slice(lastIndex, m.index)}</span>);
    }
    if (m.type === 'link') {
      parts.push(
        <a 
          key={`${keyPrefix}-link-${idx}`}
          href={m.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#D4AF37] hover:text-[#D4AF37]/80 underline underline-offset-2"
        >
          {m.text}
        </a>
      );
    } else if (m.type === 'bold') {
      parts.push(<strong key={`${keyPrefix}-bold-${idx}`} className="text-white">{m.text}</strong>);
    }
    lastIndex = m.index + m.length;
  });
  
  if (lastIndex < text.length) {
    parts.push(<span key={`${keyPrefix}-end`}>{text.slice(lastIndex)}</span>);
  }
  
  return parts.length > 0 ? parts : text;
};

// Markdown-like content renderer
const ArticleRenderer = ({ content }) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    
    // H2
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="font-['Playfair_Display',serif] text-3xl md:text-4xl text-white mt-16 mb-6 !leading-tight">
          {line.replace('## ', '')}
        </h2>
      );
    }
    // H3
    else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="font-['Barlow_Condensed'] text-2xl text-[#D4AF37] uppercase tracking-wider mt-12 mb-4">
          {line.replace('### ', '')}
        </h3>
      );
    }
    // HR
    else if (line.startsWith('---')) {
      elements.push(
        <div key={i} className="w-16 h-[2px] bg-[#D4AF37]/50 my-12" />
      );
    }
    // Unordered list item
    else if (line.startsWith('- ')) {
      const listItems = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(lines[i].replace('- ', ''));
        i++;
      }
      i--;
      elements.push(
        <ul key={i} className="space-y-2 my-6 ml-4">
          {listItems.map((item, idx) => (
            <li key={idx} className="text-white/70 flex items-start gap-3">
              <span className="text-[#D4AF37] mt-1.5">•</span>
              <span>{parseInlineElements(item, `list-${i}-${idx}`)}</span>
            </li>
          ))}
        </ul>
      );
    }
    // Bold paragraph
    else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={i} className="text-white font-bold text-xl my-6">
          {line.replace(/\*\*/g, '')}
        </p>
      );
    }
    // Italic paragraph (quote style)
    else if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
      elements.push(
        <blockquote key={i} className="border-l-4 border-[#D4AF37] pl-6 my-8 italic text-white/60 text-xl">
          {line.replace(/\*/g, '')}
        </blockquote>
      );
    }
    // Regular paragraph
    else if (line.trim() !== '') {
      elements.push(
        <p key={i} className="text-white/70 text-lg leading-relaxed my-4">
          {parseInlineElements(line, `p-${i}`)}
        </p>
      );
    }
    
    i++;
  }

  return <>{elements}</>;
};

// Affiliate box component
const AffiliateBox = ({ affiliate }) => {
  const handleOutboundClick = (url) => {
    // Track outbound click (could integrate with analytics)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'outbound',
        event_label: url,
      });
    }
  };

  return (
    <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-8 md:p-10">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[#D4AF37] text-xs uppercase tracking-wider font-medium">
          Recommended Tool
        </span>
      </div>
      
      <h3 className="font-['Playfair_Display',serif] text-2xl text-white mb-3">
        {affiliate.product}
      </h3>
      
      <p className="text-white/60 mb-6">
        {affiliate.description}
      </p>
      
      <a
        href={affiliate.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleOutboundClick(affiliate.link)}
        className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-bold hover:bg-[#D4AF37]/90 transition-colors mb-8"
      >
        Get Started
        <ExternalLink className="w-4 h-4" />
      </a>
      
      {/* Promo Codes */}
      {affiliate.promoCodes && (
        <div className="space-y-4 pt-6 border-t border-[#D4AF37]/20">
          <p className="text-[#D4AF37] text-sm uppercase tracking-wider font-medium">
            Active Promo Codes — Save 10% Your First Year
          </p>
          
          {affiliate.promoCodes.map((promo, idx) => (
            <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-4">
              <p className="text-white font-medium mb-2">{promo.plan}</p>
              <div className="flex flex-wrap gap-2">
                {promo.codes.map((code, codeIdx) => (
                  <code 
                    key={codeIdx}
                    className="bg-[#121212] border border-[#D4AF37]/30 px-3 py-1 text-[#D4AF37] text-sm font-mono"
                  >
                    {code}
                  </code>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CarrierChroniclesArticlePage;
